import { Link, useLocation } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { BlockView, useDocumentMeta } from '../components/Prose';
import { formatDate } from '../lib/format';
import { useSession } from '../state/session';
import { LEGAL_DOCUMENTS, legalBySlug } from '../content/legal';

/** Anchors so a link can point at a specific clause — `/terms#retention`
 *  is referenced from the billing API, and a page that cannot be deep-linked
 *  makes that reference useless. */
const ANCHORS: Record<string, string> = {
  '5. Fees': 'fees',
  '6. Automatic renewal, and how to stop it': 'renewal',
  '8. Retention, export and wind-down': 'retention',
  '10. Liability': 'liability',
  '13. Governing law': 'law',
  '4. How long we keep it': 'retention',
  '5. Your rights, and one honest limit': 'rights',
  '3. Who we share it with': 'subprocessors',
  '7. California privacy rights': 'california',
  '8. Cookies and analytics': 'cookies',
};

export function Legal() {
  // The routes are `/terms` and `/privacy` rather than a parameterised path,
  // so the slug comes from the location rather than from a route param.
  const { pathname } = useLocation();
  const { user } = useSession();
  const doc = legalBySlug(pathname.replace(/^\/+|\/+$/g, ''));

  useDocumentMeta(
    doc ? `${doc.title} — eSignLoop` : 'Not found — eSignLoop',
    doc?.description ?? '');

  const nav = [
    { to: '/pricing', label: 'Pricing' },
    { to: '/blog', label: 'Blog' },
    { to: '/verify', label: 'Verify a document' },
  ];
  const actions = user
    ? <Link className="btn btn--primary btn--small" to="/app">Dashboard</Link>
    : <Link className="btn btn--primary btn--small" to="/register">Sign up</Link>;

  if (!doc) {
    return (
      <AppShell nav={nav} actions={actions}>
        <section className="shell section center">
          <h1>We could not find that page</h1>
          <Link className="btn btn--primary" to="/">Back to the home page</Link>
        </section>
      </AppShell>
    );
  }

  return (
    <AppShell nav={nav} actions={actions}>
      <article className="shell shell--narrow section">
        <header>
          <h1 style={{ margin: 0 }}>{doc.title}</h1>
          <p className="small muted" style={{ marginTop: 8 }}>
            Last updated {formatDate(doc.updated)}
            {LEGAL_DOCUMENTS.filter((other) => other.slug !== doc.slug).map((other) => (
              <span key={other.slug}>
                {' · '}<Link to={`/${other.slug}`}>{other.title}</Link>
              </span>
            ))}
          </p>
        </header>

        {/* The short version first. Nobody reads a legal page from the top,
            and the four things most likely to matter should not be buried
            under twelve numbered clauses. */}
        <div className="panel stack-s" style={{ marginTop: 24 }}>
          <strong style={{ fontSize: '.92rem' }}>In short</strong>
          <ul className="prose__list" style={{ margin: 0 }}>
            {doc.summary.map((line, index) => <li key={index}>{line}</li>)}
          </ul>
          <p className="small muted" style={{ margin: 0 }}>
            The summary is not the agreement — the clauses below are.
          </p>
        </div>

        <div className="prose">
          {doc.body.map((block, index) => (
            <BlockView
              block={block}
              key={index}
              id={block.kind === 'h' ? ANCHORS[block.text] : undefined}
            />
          ))}
        </div>
      </article>
    </AppShell>
  );
}
